import { HorizontalSlider } from "~/components/horizontal-slider";
import { VariantSelector } from "~/components/products/product-variant-selector";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import { Button } from "~/components/ui/button";
import { SwatchCrossout } from "~/components/ui/icons/swatch-crossout";
import { Label } from "~/components/ui/label";
import type { VariantData } from "~/hec/types/variant-data";
import useImageUtils from "~/hooks/useImageUtils";
import type { C_ProductInfo, Product } from "~/lib/app.types";
import { LOW_STOCK_NUM } from "~/lib/constants";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { getSelectedVariant } from "~/shop/utils/variants";

import { useVariantUrlSync } from "../hooks/use-variant-url-sync";

export const SwatchOptions = ({ selected }: { selected: string }) => {
  const { getImageLink } = useImageUtils();
  return (
    <VariantSelector.OptionValues>
      {({ option, setSelected, swatch, variationGroup }) => (
        <span
          aria-hidden="true"
          key={`${option.name}-${option.value}`}
          data-testid={`pdp-variant-swatch-${option.value}`}
          onClick={() => {
            setSelected(option.value);
          }}
          className={cn(
            option.name,
            "relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full p-1",
            selected === swatch?.color.value && "border-accent-11 border",
            variationGroup?.orderable === true ? "" : "opacity-50",
          )}
        >
          {swatch && swatch?.images?.swatch?.images?.[0]?.link && (
            <img
              src={getImageLink(swatch.images.swatch.images?.[0]?.link)}
              alt={swatch?.name}
              className="h-full w-full rounded-full object-cover"
            />
          )}
          {variationGroup && !variationGroup?.orderable && (
            <SwatchCrossout className="absolute h-10 w-10" />
          )}
        </span>
      )}
    </VariantSelector.OptionValues>
  );
};

export const GenericOptions = ({
  selected,
  allSelected,
  master,
  attributeId,
}: {
  selected: string;
  allSelected: { [key: string]: string };
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
  attributeId: string;
}) => (
  <VariantSelector.OptionValues>
    {({ option, setSelected }) => {
      return (
        <SkeletonAwait
          key={`${option.name}-${option.value}`}
          resolve={master}
          skeleton={
            <Button
              variant="outline"
              data-testid={`pdp-variant-option-${option.value}`}
              className={cn(
                "inline-block h-10 border-2 border-neutral-3 bg-white px-4 text-neutral-11 shadow-sm [&:hover]:border-variant-border",
                selected === option.value && "border-variant-border",
              )}
              onClick={() => setSelected(option.value)}
            >
              {option.name}
            </Button>
          }
        >
          {master => {
            const variant = getSelectedVariant(master, {
              ...allSelected,
              [attributeId]: option.value,
            });

            return (
              <Button
                variant="outline"
                key={`${option.name}-${option.value}`}
                data-testid={`pdp-variant-option-${option.value}`}
                disabled={!variant?.orderable}
                className={cn(
                  "inline-block h-10 border-2 border-neutral-3 bg-white px-4 text-neutral-11 shadow-sm [&:hover]:border-variant-border",
                  selected === option.value && "border-variant-border",
                  !variant?.orderable &&
                    "relative cursor-not-allowed border-[#ccc] bg-[#ccc] text-[#aaa] before:absolute before:left-[-0.3125rem] before:top-0 before:w-full before:border before:border-[#aaa] before:content-[''] before:[transform:translateY(20px)_translateX(5px)_rotate(6deg)] [&:hover]:border-[#ccc]",
                )}
                onClick={() => setSelected(option.value)}
              >
                {option.name}
              </Button>
            );
          }}
        </SkeletonAwait>
      );
    }}
  </VariantSelector.OptionValues>
);

export function ProductVariantOptions({
  product,
  master,
  wrapOptions,
}: {
  product: SerializeFrom<C_ProductInfo>;
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
  wrapOptions?: boolean;
}) {
  return (
    <div className="flex flex-col items-stretch gap-4">
      {product.variationAttributes?.map(attribute => (
        <VariantSelector.OptionSelect key={attribute.id} type={attribute.id}>
          {({ selected, attribute: currentAttribute, allSelected }) => {
            return (
              <div className="relative flex w-full flex-col items-start gap-1.5 self-stretch py-0">
                <Label
                  className="w-full text-sm font-semibold"
                  data-testid={`pdp-variant-label-${currentAttribute.id}`}
                >
                  Select {currentAttribute.name || currentAttribute.id}:{" "}
                  <span className="font-light">
                    {
                      currentAttribute.values?.find(v => v.value === selected)
                        ?.name
                    }
                  </span>
                  {Boolean(
                    product?.inventory?.stockLevel &&
                      product?.inventory?.stockLevel < LOW_STOCK_NUM,
                  ) && (
                    <span className="rounded-[3px] border border-accentalpha-7 bg-surface-accent-light px-2 text-accentalpha-11">
                      low stock
                    </span>
                  )}
                </Label>
                {wrapOptions ? (
                  <div className="flex flex-row flex-wrap gap-2">
                    <GenericOptions
                      selected={selected}
                      allSelected={allSelected}
                      master={master}
                      attributeId={attribute.id}
                    />
                  </div>
                ) : (
                  <HorizontalSlider controlsEnabled>
                    <GenericOptions
                      selected={selected}
                      allSelected={allSelected}
                      master={master}
                      attributeId={attribute.id}
                    />
                  </HorizontalSlider>
                )}
              </div>
            );
          }}
        </VariantSelector.OptionSelect>
      ))}
    </div>
  );
}

type VariantSelectorRegionProps = {
  /**
   * Either a resolved product or the loader's deferred product Promise. The
   * catch-all dispatcher defers `data` on client-side navigations (RR7 `.data`
   * single-fetch; see `deferDataRequest`), so on a tile click-through this
   * arrives unresolved. `VariantSelectorRegion` resolves it before rendering
   * the inner Selector so context + URL sync always read `variationAttributes`;
   * without this the variation selector renders nothing on client-side
   * navigation while working on a hard load (SFCC-11255).
   */
  product: SerializeFrom<C_ProductInfo> | Promise<SerializeFrom<C_ProductInfo>>;
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
  productId: string;
  suppressUrlSync?: boolean;
  onVariantChange?: (values: Record<string, string>) => void;
  /** Deferred per-variant data (inventory + imageGroups) keyed by variant
   * productId — surfaced through the Selector's context so descendants
   * (ProductImages, ProductBuyForm) can react to it without prop drilling. */
  variantDataMap?: Promise<Record<string, VariantData>>;
  /** Rendered while a deferred `product` resolves (client-side navigation). */
  fallback?: React.ReactNode;
  children: React.ReactNode;
};

type VariantSelectorInnerProps = Omit<
  VariantSelectorRegionProps,
  "fallback" | "product"
> & {
  product: SerializeFrom<C_ProductInfo>;
};

function VariantSelectorInner({
  product,
  master,
  productId,
  suppressUrlSync,
  onVariantChange,
  variantDataMap,
  children,
}: VariantSelectorInnerProps) {
  const { defaultValue, onChange } = useVariantUrlSync({
    product,
    suppressUrlSync,
    onVariantChange,
  });
  // Key on the resolved product id so the selector REMOUNTS (rather than
  // reconciles) when navigating to a different product on a client-side PDP→PDP
  // nav. Without this, the selector's `selected` state survives the navigation
  // and retains the previous product's attribute keys (e.g. `count` from a prior
  // product leaking onto an `itemSize`-only product), which then mismatch this
  // product's variants in `getSelectedVariant` and render every option as OOS
  // until a hard refresh (SFCC-11748). `product` is already resolved here (it
  // comes through `VariantSelectorRegion`'s `SkeletonAwait`), so the fresh mount
  // seeds `selected` from this product's `defaultValue`. A variant switch keeps
  // the same product id, so it does not remount.
  return (
    <VariantSelector.Selector
      key={product.id}
      product={product}
      master={master}
      productId={productId}
      defaultValue={defaultValue}
      onChange={onChange}
      variantDataMap={variantDataMap}
    >
      {children}
    </VariantSelector.Selector>
  );
}

export function VariantSelectorRegion({
  fallback,
  product,
  ...props
}: VariantSelectorRegionProps) {
  // A document request receives a resolved product and therefore renders the
  // hook-using inner component directly, outside React Router's ResolveAwait
  // subtree. Client navigations keep the optimistic fallback while the genuine
  // deferred Promise settles (SFCC-11255).
  return (
    <SkeletonAwait resolve={product} skeleton={fallback}>
      {product => <VariantSelectorInner {...props} product={product} />}
    </SkeletonAwait>
  );
}
